/**
 * SHARED VOCABULARY CONTRACT
 *
 * This file serves as the single source of truth for metadata vocabulary
 * between frontend and backend systems. Both teams must use EXACT same mappings.
 *
 * Last Updated: 2025-01-21
 * Version: 1.1.1
 */

// ============================================================================
// HAIR COLOR VOCABULARY
// ============================================================================

export const HAIR_COLOR_VOCABULARY = {
  // Standard colors
  blonde: 'Blonde',
  blond: 'Blonde',
  brown: 'Brown',
  black: 'Black',
  dark: 'Black',
  'dark hair': 'Black',
  'dark-colored': 'Black',
  red: 'Red',
  auburn: 'Auburn',
  gray: 'Gray',
  grey: 'Gray',
  white: 'White',
  blue: 'Blue',
  pink: 'Pink',
  purple: 'Purple',
  green: 'Green',

  // Complex colors → "Other" (CRITICAL: both teams use EXACT same mappings)
  'multi-color': 'Other',
  multicolor: 'Other',
  'multi-colored': 'Other',
  multicolored: 'Other',
  'colorful/multi-color': 'Other', // CRITICAL FIX: AI extracts this exact format
  colorful: 'Other',
  'colorful hair': 'Other',
  rainbow: 'Other',
  mixed: 'Other',
  ombre: 'Other',
  highlights: 'Other',
  'two-tone': 'Other',
  streaks: 'Other',
  neon: 'Other',
  bright: 'Other',
  vibrant: 'Other',
  dyed: 'Other',
  colored: 'Other',

  // Fallback
  other: 'Other',
} as const;

// ============================================================================
// CULTURAL CONTEXT MAPPING
// ============================================================================

export const CULTURAL_CONTEXT_MAP = {
  'k-pop': {
    ethnicity: 'Korean',
    musicalStyles: ['K-Pop', 'Pop'] as string[],
  },
  kpop: {
    ethnicity: 'Korean',
    musicalStyles: ['K-Pop', 'Pop'] as string[],
  },
  'k pop': {
    ethnicity: 'Korean',
    musicalStyles: ['K-Pop', 'Pop'] as string[],
  },
  'j-pop': {
    ethnicity: 'Japanese',
    musicalStyles: ['J-Pop', 'Pop'] as string[],
  },
  jpop: {
    ethnicity: 'Japanese',
    musicalStyles: ['J-Pop', 'Pop'] as string[],
  },
  'j pop': {
    ethnicity: 'Japanese',
    musicalStyles: ['J-Pop', 'Pop'] as string[],
  },
};

// ============================================================================
// MUSICAL STYLE VOCABULARY
// ============================================================================

export const MUSICAL_STYLE_VOCABULARY = {
  pop: 'Pop',
  rock: 'Rock',
  'hip-hop': 'Hip-Hop',
  'hip hop': 'Hip-Hop',
  rap: 'Hip-Hop',
  'r&b': 'R&B',
  rnb: 'R&B',
  country: 'Country',
  jazz: 'Jazz',
  electronic: 'Electronic',
  edm: 'Electronic',
  classical: 'Classical',
  'k-pop': 'K-Pop',
  kpop: 'K-Pop',
  'k pop': 'K-Pop',
  'j-pop': 'J-Pop',
  jpop: 'J-Pop',
  'j pop': 'J-Pop',
} as const;

// ============================================================================
// HAIR TEXTURE VOCABULARY
// ============================================================================

export const HAIR_TEXTURE_VOCABULARY = {
  // Straight textures
  straight: 'Straight',
  'pin-straight': 'Straight',
  sleek: 'Straight',

  // Wavy textures
  wavy: 'Wavy',
  waves: 'Wavy',
  wave: 'Wavy',

  // Curly textures
  curly: 'Curly',
  curls: 'Curly',
  curl: 'Curly',

  // Coily/Kinky textures
  coily: 'Coily',
  kinky: 'Coily',
  afro: 'Coily',

  // Fallback
  natural: 'Natural',
} as const;

// ============================================================================
// INHERITABLE FIELDS
// ============================================================================

export const INHERITABLE_FIELDS = [
  'musicalStyle',
  'personalityTraits',
  'ethnicity',
  'culturalBackground',
  'language',
  'accent',
  'vocalStyle',
  'performanceStyle',
  'genre',
  'mood',
  'energy',
  'tempo',
] as const;

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Normalize hair color using shared vocabulary
 * Both frontend and backend must use this exact function
 */
export const normalizeHairColor = (color: string): string => {
  if (!color) return 'Brown'; // Most common default

  const normalized = color.toLowerCase().trim();

  // CRITICAL DEBUG: Log normalization attempts for multi-color variants
  if (normalized.includes('multi') || normalized.includes('color')) {
    console.log(`🎨 [VOCABULARY DEBUG] Normalizing: "${color}" → "${normalized}"`);
    console.log(`🎨 [VOCABULARY DEBUG] Lookup result: ${(HAIR_COLOR_VOCABULARY as any)[normalized]}`);
  }

  return (HAIR_COLOR_VOCABULARY as any)[normalized] || 'Brown';
};

/**
 * Detect cultural context from description
 * Both frontend and backend must use this exact function
 */
export const detectCulturalContext = (
  description: string
): { ethnicity?: string; musicalStyles?: string[] } => {
  if (!description) return {};

  const descLower = description.toLowerCase();

  for (const [key, context] of Object.entries(CULTURAL_CONTEXT_MAP)) {
    if (descLower.includes(key)) {
      return context;
    }
  }

  return {};
};

/**
 * Normalize musical style using shared vocabulary
 * Both frontend and backend must use this exact function
 */
export const normalizeMusicalStyle = (style: string): string => {
  if (!style) return 'Pop'; // Most common default

  const normalized = style.toLowerCase().trim();
  return (MUSICAL_STYLE_VOCABULARY as any)[normalized] || 'Pop';
};

/**
 * Normalize hair texture using shared vocabulary
 * Both frontend and backend must use this exact function
 */
export const normalizeHairTexture = (texture: string): string => {
  if (!texture) return 'Natural'; // Default fallback

  const normalized = texture.toLowerCase().trim();
  return (HAIR_TEXTURE_VOCABULARY as any)[normalized] || 'Natural';
};

/**
 * Check if a field is inheritable
 * Both frontend and backend must use this exact function
 */
export const isInheritableField = (field: string): boolean => {
  return INHERITABLE_FIELDS.includes(field as any);
};

// ============================================================================
// VALIDATION FUNCTIONS
// ============================================================================

/**
 * Validate that vocabulary mappings are consistent
 * Run this in both frontend and backend tests
 */
export const validateVocabularyConsistency = (): boolean => {
  const hairColors = Object.values(HAIR_COLOR_VOCABULARY);
  const musicalStyles = Object.values(MUSICAL_STYLE_VOCABULARY);
  const hairTextures = Object.values(HAIR_TEXTURE_VOCABULARY);

  // Check for expected number of unique values
  const hairColorSet = new Set(hairColors);
  const musicalStyleSet = new Set(musicalStyles);
  const hairTextureSet = new Set(hairTextures);

  // Multiple keys can map to same values (e.g., 'blonde' and 'blond' both map to 'Blonde')
  // This is correct behavior for vocabulary normalization
  if (hairColorSet.size !== 12) {
    console.error(
      `❌ Expected 12 unique hair colors, got ${hairColorSet.size}`
    );
    return false;
  }

  if (musicalStyleSet.size !== 10) {
    console.error(
      `❌ Expected 10 unique musical styles, got ${musicalStyleSet.size}`
    );
    return false;
  }

  if (hairTextureSet.size !== 5) {
    console.error(
      `❌ Expected 5 unique hair textures, got ${hairTextureSet.size}`
    );
    return false;
  }

  console.log('✅ Vocabulary consistency validated (v1.1.1)');
  return true;
};

// ============================================================================
// EXPORTS
// ============================================================================

export type HairColor =
  (typeof HAIR_COLOR_VOCABULARY)[keyof typeof HAIR_COLOR_VOCABULARY];
export type HairTexture =
  (typeof HAIR_TEXTURE_VOCABULARY)[keyof typeof HAIR_TEXTURE_VOCABULARY];
export type MusicalStyle =
  (typeof MUSICAL_STYLE_VOCABULARY)[keyof typeof MUSICAL_STYLE_VOCABULARY];
export type InheritableField = (typeof INHERITABLE_FIELDS)[number];

// Version info for tracking
export const VOCABULARY_VERSION = '1.1.1';
export const LAST_UPDATED = '2025-01-21';
